import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useDomainStore } from '@/hooks/useStores';
import { ChevronDown, SignOut } from '@/icons';
import UserIcon from '@/icons/User';
import Help from '@/icons/Help';
import Sparkles from '@/icons/Sparkles';
import Star from '@/icons/Star';
import OneTimePaymentModal from '@/components/family/OneTimePaymentModal';
import './profile-menu.css';

const MENU_ICON_SIZE = 18;

/** Derive up to two initials from a nickname (spaces, separators, or camelCase). */
function nicknameInitials(nickname: string): string {
  const trimmed = nickname.trim();
  if (!trimmed) {
    return 'U';
  }

  const separated = trimmed.split(/[\s._-]+/).filter(Boolean);
  if (separated.length >= 2) {
    return `${separated[0].charAt(0)}${separated[1].charAt(0)}`.toUpperCase();
  }

  const camelParts = trimmed
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean);
  if (camelParts.length >= 2) {
    return `${camelParts[0].charAt(0)}${camelParts[1].charAt(0)}`.toUpperCase();
  }

  if (trimmed.length >= 2) {
    return trimmed.slice(0, 2).toUpperCase();
  }

  return trimmed.charAt(0).toUpperCase();
}

const ProfileMenu: React.FC = observer(() => {
  const { t } = useTranslation('nav');
  const { signOut } = useAuth();
  const domainStore = useDomainStore();
  const user = domainStore.user;
  const [showDonateModal, setShowDonateModal] = useState(false);

  const nickname = user?.nickname?.trim() || t('profileMenu.userFallback');
  const email = user?.email ?? '';
  const avatarInitials = nicknameInitials(nickname);

  return (
    <div className="profile-menu-container" data-tour="profile-menu">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="profile-menu-button" aria-label={t('profileMenu.userMenu')}>
            <div className="profile-menu-avatar" aria-hidden>
              {avatarInitials}
            </div>
            <span>{nickname}</span>
            <ChevronDown className="profile-menu-chevron" aria-hidden />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content className="profile-menu-dropdown" side="bottom" align="end" sideOffset={8}>
            <DropdownMenu.Label className="profile-menu-header">
              <div className="profile-menu-avatar profile-menu-avatar--header" aria-hidden>
                {avatarInitials}
              </div>
              <div className="profile-menu-identity">
                <div className="profile-menu-name">{nickname}</div>
                {email ? <div className="profile-menu-email">{email}</div> : null}
              </div>
            </DropdownMenu.Label>

            <DropdownMenu.Item asChild>
              <Link to="/profile" className="profile-menu-item">
                <UserIcon className="profile-menu-item-icon" width={MENU_ICON_SIZE} height={MENU_ICON_SIZE} />
                {t('profileMenu.myAccount')}
              </Link>
            </DropdownMenu.Item>

            <DropdownMenu.Item asChild>
              <Link to="/help" className="profile-menu-item">
                <Help className="profile-menu-item-icon" width={MENU_ICON_SIZE} height={MENU_ICON_SIZE} />
                {t('profileMenu.helpCenter')}
              </Link>
            </DropdownMenu.Item>

            <DropdownMenu.Item asChild>
              <Link to="/changelog" className="profile-menu-item">
                <Sparkles className="profile-menu-item-icon" width={MENU_ICON_SIZE} height={MENU_ICON_SIZE} />
                {t('profileMenu.whatsNew')}
              </Link>
            </DropdownMenu.Item>

            <DropdownMenu.Item
              className="profile-menu-item"
              onSelect={() => {
                setShowDonateModal(true);
              }}
            >
              <Star className="profile-menu-item-icon" width={MENU_ICON_SIZE} height={MENU_ICON_SIZE} />
              {t('profileMenu.supportWeTree')}
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="profile-menu-separator" />

            <DropdownMenu.Item asChild>
              <button type="button" onClick={signOut} className="profile-menu-item profile-menu-item--signout">
                <SignOut className="profile-menu-item-icon" width={MENU_ICON_SIZE} height={MENU_ICON_SIZE} />
                {t('profileMenu.signOut')}
              </button>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <OneTimePaymentModal kind="donate" isOpen={showDonateModal} onClose={() => setShowDonateModal(false)} />
    </div>
  );
});

export default ProfileMenu;
